import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Capacity } from '../models/capacity';
import { CapacityService } from '../services/capacity.service';

/**
 * Department data source
 */
export class CapacityDataSource implements DataSource<Capacity> {
  /**
   * Capacity subject
   *
   * @private
   */
  private capacitySubject = new BehaviorSubject<Capacity[]>([]);

  /**
   * Loading subject
   *
   * @private
   */
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Loading observable
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Constructor
   *
   * @param capacityService  service
   */
  constructor(private capacityService: CapacityService) {}

  /**
   * Connect to collection
   *
   * @param collectionViewer CollectionViewer
   *
   * @returns Observable
   */
  connect(_collectionViewer: CollectionViewer): Observable<Capacity[]> {
    return this.capacitySubject.asObservable();
  }

  /**
   * Disconnect from collection
   *
   * @param collectionViewer CollectionViewer
   */
  disconnect(_collectionViewer: CollectionViewer): void {
    this.capacitySubject.complete();
    this.loadingSubject.complete();
  }

  /**
   * Load data from collection
   *
   * @param order Order
   * @param sort Sort
   * @param limit Limit
   * @param page Page
   * @param filter Object with filtered attributes
   */
  load(order: string = 'id', sort: string = 'asc', limit: number = 10, page: number = 0, filter: object = {}): void {
    this.loadingSubject.next(true);

    const options = { order, sort, limit, page };

    for (const i in filter) {
      if (filter.hasOwnProperty(i) && filter[i] === '') {
        delete filter[i];
      }
    }

    this.capacityService
      .findFiltered({ ...options, ...filter })
      .pipe(
        catchError(() => of([])),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((capacities) => this.capacitySubject.next(capacities));
  }
}
